import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GenericService } from '../generic.service';
import { LandingService } from '../landing-page/service/landing.service';
import {
  FormGroup,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiComponentServices } from '../services/api-services';
import { AccountDetails } from '../models/accountData';
import { Subscription } from 'rxjs';
import { defaultLanguage } from '../shared/constants/language.constants';
import { CrowdinService } from '../shared/service/crowdin.service';

@Component({
    selector: 'app-welcome-page',
    imports: [FormsModule, ReactiveFormsModule, CommonModule, RouterModule],
    templateUrl: './welcome-page.component.html',
    styleUrl: './welcome-page.component.scss'
})
export class WelcomePageComponent {
  welcomeForm!: FormGroup;
  deviceData: any = {};
  deviceData$: any = null;
  isClearDisabled: boolean = true;
  isNextDisabled: boolean = true;
  accounts: any[] = [];
  accountNumber: string = '';
  showAlert: boolean = false;
  output: string = '';
  scanEnable: boolean = false;
  // @ViewChild(ScanQrCodeComponent) scanQrCodeComponent!: ScanQrCodeComponent;
  receivedData: string = '';
  accountDetails: AccountDetails | null = null;
  qrId: any;
  isLoading = true;
  id: string | null = null;
  languageIndex: number = 0;
  translations: { [key: string]: string } = {};
  currentLanguage: string = defaultLanguage;
  languageOptions: Array<{ value: string }> = [];
  isLoadingTranslations = true;
  subs = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private landingService: LandingService,
    private router: Router,
    private genericService: GenericService,
    private fb: FormBuilder,
    private apiService: ApiComponentServices,
    private crowdinService: CrowdinService,
  ) {
    this.deviceData$ = this.genericService.deviceData.subscribe((data) => {
      if (data) {
        this.deviceData = data;
      }
    });
  }

  async ngOnInit() {
    this.languageOptions = this.landingService.getLanguageOptions();

    const stored = localStorage.getItem('deviceData');
    const selectedName = stored
      ? JSON.parse(stored).languageSelected || defaultLanguage
      : defaultLanguage;
    this.currentLanguage = selectedName;

    this.subs.add(
      this.landingService.languageName$.subscribe((langName) => {
        this.currentLanguage = langName;
        this.translations = this.crowdinService.getAllTranslations();
      }),
    );

    this.landingService.loadTranslations(this.currentLanguage);
    this.route.paramMap.subscribe((params) => {
      this.id = params.get('id');
      this.accountNumber = params.get('accountNumber') || '';
    });
    this.qrId = this.id;
    if (this.qrId) {
      localStorage.setItem('qrId', this.qrId);
    } else {
      this.qrId = localStorage.getItem('qrId');
    }
    this.isQrMapped();

    this.initializeForm();
  }

  async languageChange(event: any) {
    const selectedLang = event.target.value;
    await this.landingService.loadTranslations(selectedLang);
  }

  initializeForm() {
    this.welcomeForm = this.fb.group({
      digit1: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit2: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit3: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit4: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit5: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit6: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit7: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit8: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit9: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      digit10: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    });
  }

  onAccountNumberEntry(event: Event, nextElementId: string) {
    if (
      this.digit1.value?.toLocaleString() ||
      this.digit2.value?.toLocaleString() ||
      this.digit3.value?.toLocaleString() ||
      this.digit4.value?.toLocaleString() ||
      this.digit5.value?.toLocaleString() ||
      this.digit6.value?.toLocaleString() ||
      this.digit7.value?.toLocaleString() ||
      this.digit8.value?.toLocaleString() ||
      this.digit9.value?.toLocaleString() ||
      this.digit10.value?.toLocaleString()
    )
      this.isClearDisabled = false;
    else this.isClearDisabled = true;

    if (
      this.digit1.value?.toLocaleString() != '' &&
      this.digit2.value?.toLocaleString() != '' &&
      this.digit3.value?.toLocaleString() != '' &&
      this.digit4.value?.toLocaleString() != '' &&
      this.digit5.value?.toLocaleString() != '' &&
      this.digit6.value?.toLocaleString() != '' &&
      this.digit7.value?.toLocaleString() != '' &&
      this.digit8.value?.toLocaleString() != '' &&
      this.digit9.value?.toLocaleString() != '' &&
      this.digit10.value?.toLocaleString() != ''
    )
      this.isNextDisabled = false;
    else this.isNextDisabled = true;
    this.moveFocus(event, nextElementId);
  }

  get digit1() {
    return this.welcomeForm.get('digit1') as FormControl;
  }
  get digit2() {
    return this.welcomeForm.get('digit2') as FormControl;
  }
  get digit3() {
    return this.welcomeForm.get('digit3') as FormControl;
  }
  get digit4() {
    return this.welcomeForm.get('digit4') as FormControl;
  }
  get digit5() {
    return this.welcomeForm.get('digit5') as FormControl;
  }
  get digit6() {
    return this.welcomeForm.get('digit6') as FormControl;
  }
  get digit7() {
    return this.welcomeForm.get('digit7') as FormControl;
  }
  get digit8() {
    return this.welcomeForm.get('digit8') as FormControl;
  }
  get digit9() {
    return this.welcomeForm.get('digit9') as FormControl;
  }
  get digit10() {
    return this.welcomeForm.get('digit10') as FormControl;
  }
  get scanData() {
    return this.welcomeForm.get('scanData') as FormControl;
  }

  clear() {
    this.digit1.setValue('');
    this.digit2.setValue('');
    this.digit3.setValue('');
    this.digit4.setValue('');
    this.digit5.setValue('');
    this.digit6.setValue('');
    this.digit7.setValue('');
    this.digit8.setValue('');
    this.digit9.setValue('');
    this.digit10.setValue('');
    this.showAlert = false;
    this.isNextDisabled = true;
  }

  onKeydown(
    event: KeyboardEvent,
    currentElement: string,
    nextElementId: string,
  ) {
    if (event && event.key === 'Backspace') {
      this.welcomeForm.controls[currentElement].setValue('');
      this.isNextDisabled = true;
      const nextInput = document.getElementById(
        nextElementId,
      ) as HTMLInputElement;
      if (nextInput) {
        event.preventDefault();
        (nextInput as HTMLInputElement).focus();
      }
    }
  }

  nextClick() {
    this.getAccountId();
    this.updateAccountData();
    this.router.navigate(['/accountDetails']);
  }

  getAccountId() {
    this.accountNumber =
      this.digit1.value?.toLocaleString() +
      this.digit2.value?.toLocaleString() +
      this.digit3.value?.toLocaleString() +
      this.digit4.value?.toLocaleString() +
      this.digit5.value?.toLocaleString() +
      this.digit6.value?.toLocaleString() +
      this.digit7.value?.toLocaleString() +
      this.digit8.value?.toLocaleString() +
      this.digit9.value?.toLocaleString() +
      this.digit10.value?.toLocaleString();
  }

  getQrDetails(qrId: string | null): void {
    if (!qrId) {
      return;
    }

    this.landingService.getQrDetails(qrId).subscribe((qrDetails) => {
      this.accounts = qrDetails;
      const ids = Object.keys(qrDetails);
      ids.forEach((element: any) => {
        let accountId = this.accounts[element].ecolabAccountId;
        if (accountId == this.accountNumber) {
          this.landingPage(element);
        } else {
          this.showAlert = true;
        }
      });
    });
  }

  landingPage(qrId: any) {
    const originalUrl: string = this.router.url;
    let url = originalUrl + qrId;
    this.router.navigate([url]);
  }

  // toggleScanner() {
  //   this.scanEnable = true;
  // }

  // handleData(data: string = '1234567899') {
  //   this.receivedData = data;
  //   if (this.receivedData) {
  //     this.scanEnable = false;
  //     this.accountNumber = this.receivedData;
  //     this.fillAccountNumber(this.receivedData);
  //     this.isClearDisabled = false;
  //     this.isNextDisabled = false;
  //   }
  // }

  getQrId(url: any) {
    if (url) {
      const parts: string[] = url.split('/'); // Split the URL by '/'
      const qrId: string | undefined = parts.pop(); // Remove the last part and store it
      if (qrId) {
        this.landingService.getQrDetails(qrId).subscribe((qrDetails) => {
          let accountId = qrDetails[qrId].ecolabAccountId;
          if (accountId) {
            this.fillAccountNumber(accountId);
            this.isNextDisabled = false;
            this.isClearDisabled = false;
            // this.router.navigate(['/' + qrId]);
          } else {
            this.fillAccountNumber(accountId);
            this.showAlert = true;
          }
        });
      }
    }
  }

  moveFocus(event: Event, nextElementId: string) {
    const input = event.target as HTMLInputElement;
    if (input.value.length === input.maxLength) {
      const nextInput = document.getElementById(
        nextElementId,
      ) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  validateLength(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    if (
      input.value.length >= 1 ||
      event.key === '.' ||
      event.key === '-' ||
      event.key === '+'
    ) {
      event.preventDefault();
    }
  }

  fillAccountNumber(accountNumber: any) {
    if (accountNumber) {
      let number = Array.from(accountNumber);
      this.digit1.setValue(number[0]);
      this.digit2.setValue(number[1]);
      this.digit3.setValue(number[2]);
      this.digit4.setValue(number[3]);
      this.digit5.setValue(number[4]);
      this.digit6.setValue(number[5]);
      this.digit7.setValue(number[6]);
      this.digit8.setValue(number[7]);
      this.digit9.setValue(number[8]);
      this.digit10.setValue(number[9]);
    }
  }

  updateAccountData() {
    this.deviceData['QRId'] = this.qrId;
    this.deviceData['accountNumber'] = this.accountNumber;
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
  }

  isQrMapped() {
    const accountNumber = this.deviceData?.accountNumber || this.accountNumber;
    const qr = this.deviceData.QRId || this.qrId;
    this.apiService.getQRMappedInfo(this.qrId).subscribe((response) => {
      if (response) {
        if (response?.AccountNumber != null && response?.IsQRVerified) {
          this.deviceData['QRId'] = this.qrId;
          this.deviceData['accountNumber'] = response?.AccountNumber;
          this.deviceData['languageSelected'] = this.currentLanguage;
          this.deviceData['address'] = response?.Address;
          this.deviceData['businessName'] = response?.AccountName;
          this.deviceData['city'] = response?.City;
          this.deviceData['state'] = response?.State;
          this.deviceData['zip'] = response?.ZipCode;
          this.deviceData['devices'] = response?.Devices.map(
            (device: { MachineType: any; MachineName: any }) => ({
              machineType: device.MachineType,
              machineName: device.MachineName,
            }),
          );
          this.genericService.setLocalStorage(this.deviceData);
          this.genericService.deviceData.next(this.deviceData);

          this.router.navigate([`troubleshoot/${accountNumber}`]);
        }
      }
      this.isLoading = false;
    });
  }
}
